<template>
    <svg class="mark" :class="{ 'is-animating': animate }" viewBox="0 0 30 14" fill="none" aria-hidden="true" focusable="false">
        <circle class="dot dot-1" style="--i: 0" cx="7" cy="7" r="7" />
        <circle class="dot dot-2" style="--i: 1" cx="15" cy="7" r="7" />
        <circle class="dot dot-3" style="--i: 2" cx="23" cy="7" r="7" />
        <defs>
            <clipPath :id="`${uid}-a`"><circle class="clipdot" style="--i: 0" cx="7" cy="7" r="7" /></clipPath>
            <clipPath :id="`${uid}-b`"><circle class="clipdot" style="--i: 1" cx="15" cy="7" r="7" /></clipPath>
            <clipPath :id="`${uid}-c`"><circle class="clipdot" style="--i: 2" cx="23" cy="7" r="7" /></clipPath>
        </defs>
        <!-- Transform only the clip circles. Transforming a clipped group or rectangle also transforms its
             clip path, doubling the bounce and misaligning overlaps.
             -->
        <g :clip-path="`url(#${uid}-a)`"><rect class="overlap overlap-1" style="--i: 1" x="-900" y="-900" width="1800" height="1800" :clip-path="`url(#${uid}-b)`" /></g>
        <g :clip-path="`url(#${uid}-b)`"><rect class="overlap overlap-2" style="--i: 2" x="-900" y="-900" width="1800" height="1800" :clip-path="`url(#${uid}-c)`" /></g>
    </svg>
</template>

<script setup lang="ts">
/**
 * Paint overlaps explicitly to avoid antialiased rims from blending. Use document-unique clip IDs so
 * multiple marks cannot reference each other's circles.
 */
import { useId } from "vue";

const { animate = true } = defineProps<{ animate?: boolean }>();
const uid = useId();
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* The prototype's 30x14 geometry at 2.14x, and overflow visible so the bounce
       can leave the box. */
    .mark {
        @apply block h-[30px] w-[64.286px] shrink-0 overflow-visible;
    }

    .dot-1 {
        fill: var(--color-setup-marker-pink);
    }

    .dot-2 {
        fill: var(--color-setup-marker-amber);
    }

    .dot-3 {
        fill: var(--color-setup-marker-teal);
    }

    .overlap-1 {
        fill: var(--color-setup-marker-pink-over);
    }

    .overlap-2 {
        fill: var(--color-setup-marker-amber-over);
    }
}

@media (prefers-reduced-motion: no-preference) {
    .mark.is-animating .dot,
    .mark.is-animating .clipdot {
        transform-box: fill-box;
        transform-origin: 50% 100%;
        animation: perspective-mark-drop 0.72s both;
        animation-delay: calc(0.1s + var(--i) * 0.1s);
    }

    .mark.is-animating .overlap {
        animation: perspective-mark-fade 0.14s linear both;
        animation-delay: calc(0.1s + var(--i) * 0.1s);
    }
}

@keyframes perspective-mark-drop {
    0% {
        opacity: 0;
        transform: translateY(-11px) scale(0.92, 0.92);
        animation-timing-function: cubic-bezier(0.55, 0.06, 0.68, 0.19);
    }
    18% {
        opacity: 1;
    }
    45% {
        transform: translateY(0) scale(1.12, 0.86);
        animation-timing-function: cubic-bezier(0.33, 0, 0.67, 1);
    }
    62% {
        transform: translateY(-3.2px) scale(1, 1);
        animation-timing-function: cubic-bezier(0.55, 0.06, 0.68, 0.19);
    }
    78% {
        transform: translateY(0) scale(1.06, 0.94);
        animation-timing-function: cubic-bezier(0.33, 0, 0.67, 1);
    }
    90% {
        transform: translateY(-1.1px) scale(1, 1);
    }
    100% {
        opacity: 1;
        transform: translateY(0) scale(1, 1);
    }
}

@keyframes perspective-mark-fade {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
</style>
